import { useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { BackgroundColorsMap, MilanBackgroundColors } from '@milanlaser/mlcomponents';

type TextAlign = 'left' | 'center' | 'right';

interface PromotionsBannerData {
    showCountdownTimer?: boolean | null;
    specialsBanner?: string | null;
    linkTo?: string | null;
    backgroundColor?: string | null;
    textAlign?: string | null;
}

// startDate/endDate here are the entry's Builder schedule, not the data fields.
interface PromotionsBannerEntry {
    startDate?: number | null;
    endDate?: number | null;
    data: PromotionsBannerData | null;
}

interface RawPromotionsBannerEntry {
    startDate?: number | null;
    endDate?: number | null;
    content?: { data?: PromotionsBannerData | null } | null;
}

interface PromoCountdownQuery {
    site: { buildTime: string };
    allBuilderModels: {
        promotionsBanners: RawPromotionsBannerEntry[] | null;
    };
}

export interface PromoCountdownConfig {
    startMs: number | null;
    endMs: number;
    promoText: string;
    linkTo: string;
    backgroundColor: MilanBackgroundColors;
    textAlign: TextAlign;
}

const DEFAULT_LINK = '/specials/';
const DEFAULT_BACKGROUND: MilanBackgroundColors = 'Navy';
const TEXT_ALIGNMENTS: TextAlign[] = ['left', 'center', 'right'];

// 'default' maps to an empty class, which would leave the banner transparent.
function isBackgroundColor(value: string | null | undefined): value is MilanBackgroundColors {
    return !!value && value !== 'default' && Object.prototype.hasOwnProperty.call(BackgroundColorsMap, value);
}

function isTextAlign(value: string | null | undefined): value is TextAlign {
    return TEXT_ALIGNMENTS.includes(value as TextAlign);
}

function isNumber(value: unknown): value is number {
    return typeof value === 'number';
}

// Builder's entry-level Start/End (the Scheduling panel) is the single source
// of truth for the window. Marketing uses it to publish/unpublish the entry
// via webhook, and the same dates drive this selection so the two can never
// drift. An entry's data.startDate/endDate fields are ignored.
//
// An entry published without a schedule has no window, so it counts as live
// from the moment it's published: the newest live entry wins, scheduled or
// not. Publishing a newer pencil banner therefore replaces a running countdown.
function hasStarted(entry: PromotionsBannerEntry | null | undefined, nowMs: number): boolean {
    return !isNumber(entry?.startDate) || nowMs >= entry.startDate;
}
function hasEnded(entry: PromotionsBannerEntry | null | undefined, nowMs: number): boolean {
    return isNumber(entry?.endDate) && nowMs >= entry.endDate;
}
function isLive(entry: PromotionsBannerEntry | null | undefined, nowMs: number): boolean {
    return hasStarted(entry, nowMs) && !hasEnded(entry, nowMs);
}

/**
 * The entry live at nowMs: the first one in list order (createdDate desc,
 * matching Builder's content-list UI) that is either unscheduled or inside its
 * scheduled window. A scheduled entry that hasn't started yet doesn't preempt
 * an older live one; an expired entry falls through to the next entry down.
 * Drives the countdown (via mapPromoCountdown, which needs a scheduled end).
 */
export function selectLiveEntry<T extends PromotionsBannerEntry>(
    entries: T[] | null | undefined,
    nowMs: number
): T | null {
    return entries?.find((entry) => isLive(entry, nowMs)) ?? null;
}

/**
 * The entry currently owning the PENCIL BANNER: the live entry, or — when
 * every entry is expired or not yet started — the first one in list order that
 * has started, so the banner slot never goes blank. That fallback is expired
 * promo copy, not necessarily the most recently ended. Used by the runtime fetch.
 */
export function selectCurrentPencilEntry<T extends PromotionsBannerEntry>(
    entries: T[] | null | undefined,
    nowMs: number
): T | null {
    return selectLiveEntry(entries, nowMs) ?? entries?.find((entry) => hasStarted(entry, nowMs)) ?? null;
}

/**
 * Maps a promotions-banners entry to countdown props, or null when the entry
 * has no scheduled window, no copy, or has the countdown toggled off. A null
 * return here falls back to the pencil banner for that period, which is what
 * marketing gets when they explicitly disable the countdown on the top entry.
 */
export function mapPromoCountdown(entry: PromotionsBannerEntry | null | undefined): PromoCountdownConfig | null {
    const data = entry?.data;
    if (!data || data.showCountdownTimer !== true) return null;
    // Blank copy would render a timer-only bar; fall back to the pencil banner.
    const promoText = data.specialsBanner?.trim();
    if (!promoText) return null;
    // No scheduled end → no window to count down to.
    if (!isNumber(entry.endDate)) return null;

    return {
        startMs: isNumber(entry.startDate) ? entry.startDate : null,
        endMs: entry.endDate,
        promoText,
        linkTo: data.linkTo || DEFAULT_LINK,
        backgroundColor: isBackgroundColor(data.backgroundColor) ? data.backgroundColor : DEFAULT_BACKGROUND,
        textAlign: isTextAlign(data.textAlign) ? data.textAlign : 'center',
    };
}

// Mirrored after mount by useCountdownActive's started/expired timers; keep the
// boundaries (start inclusive, end exclusive) in sync.
export function isWithinCountdownWindow(countdown: PromoCountdownConfig, nowMs: number): boolean {
    return (countdown.startMs === null || nowMs >= countdown.startMs) && nowMs < countdown.endMs;
}

/**
 * Countdown config sourced at build time — publishing the promotions-banners
 * model in Builder, or a scheduled entry starting or ending, triggers a
 * rebuild via webhook. buildTime picks the live entry and lets the server
 * render decide the window deterministically (see useCountdownActive).
 *
 * Only built-in fields are queried (the entry-level schedule plus the raw
 * content JSON), not typed data fields: Gatsby validates static queries
 * against the Builder space's schema, so a space whose model lacks a
 * countdown field (e.g. QA) would otherwise fail the whole build. A missing
 * data field just leaves the countdown off.
 */
export function usePromoCountdown(): { countdown: PromoCountdownConfig | null; buildTimeMs: number } {
    const { site, allBuilderModels } = useStaticQuery<PromoCountdownQuery>(graphql`
        query {
            site {
                buildTime
            }
            allBuilderModels {
                promotionsBanners(limit: 10, sort: { createdDate: -1 }, options: { cachebust: true }) {
                    startDate
                    endDate
                    content
                }
            }
        }
    `);

    const rawEntries = allBuilderModels?.promotionsBanners;
    const buildTimeMs = Date.parse(site.buildTime);

    return useMemo(() => {
        const entries: PromotionsBannerEntry[] | undefined = rawEntries?.map(({ startDate, endDate, content }) => ({
            startDate,
            endDate,
            data: content?.data ?? null,
        }));
        return { countdown: mapPromoCountdown(selectLiveEntry(entries, buildTimeMs)), buildTimeMs };
    }, [rawEntries, buildTimeMs]);
}
